import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import toast from 'react-hot-toast'
import {
  ArrowPathIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline'
import { PrinterSettingsFields, emptyForm } from '../../components/printer/PrinterSettingsFields'

export default function TenantPrinters({ fixedGroupId = '', embedded = false }) {
  const [tenants, setTenants] = useState([])
  const [printers, setPrinters] = useState([])
  const [loading, setLoading] = useState(false)
  const [tenantsLoading, setTenantsLoading] = useState(!embedded)
  const [groupId, setGroupId] = useState(fixedGroupId || '')

  useEffect(() => {
    if (fixedGroupId) setGroupId(fixedGroupId)
  }, [fixedGroupId])
  const [selected, setSelected] = useState(null)
  const [form, setForm] = useState(emptyForm())
  const [saving, setSaving] = useState(false)
  const [tenantDevices, setTenantDevices] = useState([])
  const [linkDeviceId, setLinkDeviceId] = useState('')
  const [linking, setLinking] = useState(false)

  const loadTenants = useCallback(async () => {
    setTenantsLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/admin/tenants'))
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setTenants(Array.isArray(data.tenants) ? data.tenants : [])
    } catch (e) {
      toast.error(e.message || 'Failed to load tenants')
      setTenants([])
    } finally {
      setTenantsLoading(false)
    }
  }, [])

  const loadPrinters = useCallback(async () => {
    setLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/admin/printers/all'))
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setPrinters(Array.isArray(data.data) ? data.data : [])
    } catch (e) {
      toast.error(e.message || 'Failed to load printers')
      setPrinters([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!embedded) loadTenants()
  }, [loadTenants, embedded])

  useEffect(() => {
    loadPrinters()
  }, [loadPrinters])

  const loadTenantDevices = useCallback(async (gid) => {
    if (!gid) {
      setTenantDevices([])
      return
    }
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/admin/tenants/${encodeURIComponent(gid)}/mobile-devices?status=all`)
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setTenantDevices(Array.isArray(data.data) ? data.data : [])
    } catch (e) {
      toast.error(e.message || 'Failed to load devices')
      setTenantDevices([])
    }
  }, [])

  useEffect(() => {
    if (selected?.id && !selected?.mobile_device_id && selected?.group_id) {
      loadTenantDevices(selected.group_id)
      setLinkDeviceId('')
    } else {
      setTenantDevices([])
      setLinkDeviceId('')
    }
  }, [selected?.id, selected?.mobile_device_id, selected?.group_id, loadTenantDevices])

  const sortPrinterRows = (rows) =>
    [...rows].sort((a, b) => {
      const ta = String(a.tenant_name || a.group_id)
      const tb = String(b.tenant_name || b.group_id)
      if (ta !== tb) return ta.localeCompare(tb)
      return (a.sort_order ?? 0) - (b.sort_order ?? 0)
    })

  const filtered = useMemo(() => {
    let rows = [...printers]
    if (groupId.trim()) {
      rows = rows.filter((p) => p.group_id === groupId.trim())
    }
    return sortPrinterRows(rows)
  }, [printers, groupId])

  const independentPrinters = useMemo(
    () => sortPrinterRows(filtered.filter((p) => !p.mobile_device_id)),
    [filtered]
  )
  const deviceLinkedPrinters = useMemo(
    () => sortPrinterRows(filtered.filter((p) => !!p.mobile_device_id)),
    [filtered]
  )

  const renderPrinterRow = (p) => {
    const active = selected?.id === p.id
    return (
      <li key={p.id}>
        <button
          type="button"
          onClick={() => setSelected(p)}
          className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${active ? 'bg-psg-teal/5 border-l-4 border-psg-teal' : 'border-l-4 border-transparent'}`}
        >
          <div className="font-medium text-sm text-gray-900 truncate">{p.label || 'Unnamed printer'}</div>
          <div className="text-[10px] font-mono text-gray-500 mt-0.5">{p.group_id}</div>
          <div className="text-xs text-gray-600 truncate">{p.tenant_name || p.group_id}</div>
          {p.mobile_device_id ? (
            <div className="text-[10px] font-mono text-amber-800 mt-1 truncate" title="Device-specific row">
              Device: {p.mobile_device_id}
            </div>
          ) : (
            <div className="text-[10px] text-gray-400 mt-1">Independent · tenant default</div>
          )}
        </button>
      </li>
    )
  }

  useEffect(() => {
    if (!selected) {
      setForm(emptyForm())
      return
    }
    setForm({
      label: selected.label || '',
      enable_printer: !!selected.enable_printer,
      printer_default_gateway: selected.printer_default_gateway || '',
      printer_ip_address: selected.printer_ip_address || '',
      printer_ip_subnet_mask: selected.printer_ip_subnet_mask || '',
      printer_model_number: selected.printer_model_number || '',
      printer_port_number: selected.printer_port_number || '',
      enable_payment: !!selected.enable_payment,
      printer_brand: selected.printer_brand || 'STAR',
      printer_model_kind:
        selected.printer_model_kind === 'usb_desktop' ? 'bt_portable' : selected.printer_model_kind || 'bt_portable',
      gsl_type: selected.gsl_type || 'api',
      enable_gsl_receipt: !!selected.enable_gsl_receipt,
      sort_order: selected.sort_order ?? 0,
    })
  }, [selected])

  const patchField = (key, val) => setForm((f) => ({ ...f, [key]: val }))

  const save = async () => {
    const gid = (selected || {}).group_id || groupId.trim()
    if (!gid) {
      toast.error('Select or enter a tenant GSID first')
      return
    }
    setSaving(true)
    try {
      if (selected?.id) {
        const res = await authFetch(
          getLocalApiUrl(`/api/admin/tenants/${encodeURIComponent(gid)}/printers/${encodeURIComponent(selected.id)}`),
          { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) }
        )
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
        toast.success('Printer saved')
        await loadPrinters()
        if (data.data) {
          setSelected({
            ...data.data,
            tenant_name:
              tenants.find((t) => t.group_id === data.data.group_id)?.name || data.data.group_id,
          })
        }
      } else {
        const res = await authFetch(getLocalApiUrl(`/api/admin/tenants/${encodeURIComponent(gid)}/printers`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
        toast.success('Printer created')
        await loadPrinters()
        if (data.data) {
          setSelected({
            ...data.data,
            tenant_name:
              tenants.find((t) => t.group_id === data.data.group_id)?.name || data.data.group_id,
          })
        }
      }
    } catch (e) {
      toast.error(e.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const linkIndependentToDevice = async () => {
    if (!selected?.id || !selected?.group_id) return
    const did = linkDeviceId.trim()
    if (!did) {
      toast.error('Choose a device to link')
      return
    }
    setLinking(true)
    try {
      const res = await authFetch(
        getLocalApiUrl(
          `/api/admin/tenants/${encodeURIComponent(selected.group_id)}/printers/${encodeURIComponent(selected.id)}`
        ),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mobile_device_id: did }),
        }
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      toast.success('Printer linked to device')
      await loadPrinters()
      if (data.data) {
        setSelected({
          ...data.data,
          tenant_name:
            tenants.find((t) => t.group_id === data.data.group_id)?.name || data.data.group_id,
        })
      }
    } catch (e) {
      toast.error(e.message || 'Link failed')
    } finally {
      setLinking(false)
    }
  }

  const unlinkPrinterFromDevice = async () => {
    if (!selected?.id || !selected?.group_id || !selected?.mobile_device_id) return
    if (
      !window.confirm(
        'This row will become an independent tenant default again (not tied to a device). Continue?'
      )
    ) {
      return
    }
    setLinking(true)
    try {
      const res = await authFetch(
        getLocalApiUrl(
          `/api/admin/tenants/${encodeURIComponent(selected.group_id)}/printers/${encodeURIComponent(selected.id)}`
        ),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mobile_device_id: null }),
        }
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      toast.success('Printer is now independent')
      await loadPrinters()
      if (data.data) {
        setSelected({
          ...data.data,
          tenant_name:
            tenants.find((t) => t.group_id === data.data.group_id)?.name || data.data.group_id,
        })
      }
    } catch (e) {
      toast.error(e.message || 'Unlink failed')
    } finally {
      setLinking(false)
    }
  }

  const remove = async (row) => {
    if (!window.confirm(`Delete printer "${row.label || row.id}" for ${row.group_id}?`)) return
    try {
      const res = await authFetch(
        getLocalApiUrl(
          `/api/admin/tenants/${encodeURIComponent(row.group_id)}/printers/${encodeURIComponent(row.id)}`
        ),
        { method: 'DELETE' }
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      toast.success('Deleted')
      setSelected(null)
      await loadPrinters()
    } catch (e) {
      toast.error(e.message || 'Delete failed')
    }
  }

  const startCreate = () => {
    const gid = groupId.trim()
    if (!gid) {
      toast.error('Enter tenant GSID in the filter (or pick from list) before adding a printer')
      return
    }
    setSelected({ id: null, group_id: gid, tenant_name: tenants.find((t) => t.group_id === gid)?.name || gid })
    setForm(emptyForm())
  }

  return (
    <div className={embedded ? 'min-h-[480px] flex flex-col bg-gray-50 rounded-lg border border-gray-200' : 'h-screen flex flex-col bg-gray-50'}>
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          {!embedded && (
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-psg-teal to-psg-teal/80 flex items-center justify-center">
                <PrinterIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Tenant printers (all tenants)</h1>
                <p className="text-xs text-gray-500">Filter by GSID or manage per tenant from the Tenants list.</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap ml-auto">
            {!embedded && (
            <>
            <input
              type="text"
              list="tenant-printer-gsids"
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono min-w-[200px]"
              placeholder={tenantsLoading ? 'Loading tenants…' : 'Filter by tenant GSID (optional)'}
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              disabled={tenantsLoading}
            />
            <datalist id="tenant-printer-gsids">
              {tenants.map((t) => (
                <option key={t.group_id} value={t.group_id} />
              ))}
            </datalist>
            </>
            )}
            <button
              type="button"
              onClick={startCreate}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
            >
              <PlusIcon className="h-4 w-4" />
              Add independent printer
            </button>
            <button
              type="button"
              onClick={loadPrinters}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg bg-psg-teal px-4 py-2 text-sm font-medium text-white hover:bg-psg-teal/90 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden min-h-0">
        <div className="w-96 border-r border-gray-200 bg-white overflow-y-auto">
          {loading && printers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-400">
              <ArrowPathIcon className="h-10 w-10 animate-spin mb-2" />
              <p className="text-sm">Loading…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-gray-500 text-sm">No printers{groupId.trim() ? ' for this filter' : ''}.</div>
          ) : (
            <div className="pb-8">
              <div className="sticky top-0 z-10 bg-slate-100 border-b border-slate-200 px-4 py-2">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                  Independent (tenant defaults)
                </div>
                <div className="text-[10px] text-slate-500">{independentPrinters.length} printer(s)</div>
              </div>
              <ul className="divide-y divide-gray-100">
                {independentPrinters.length === 0 ? (
                  <li className="px-4 py-3 text-xs text-gray-400">None for this filter.</li>
                ) : (
                  independentPrinters.map((p) => renderPrinterRow(p))
                )}
              </ul>

              <div className="sticky top-0 z-10 bg-amber-50 border-y border-amber-100 px-4 py-2 mt-1">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-900">
                  Linked to a device
                </div>
                <div className="text-[10px] text-amber-800/90">
                  {deviceLinkedPrinters.length} printer(s) — scoped to one device
                </div>
              </div>
              <ul className="divide-y divide-gray-100">
                {deviceLinkedPrinters.length === 0 ? (
                  <li className="px-4 py-3 text-xs text-gray-400">
                    None yet. Configure under Device approvals when a device is selected.
                  </li>
                ) : (
                  deviceLinkedPrinters.map((p) => renderPrinterRow(p))
                )}
              </ul>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
          {!selected ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <PrinterIcon className="h-16 w-16 mb-3 opacity-40" />
              <p className="text-sm font-medium text-gray-600">Select a printer or add one</p>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto space-y-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selected.id ? 'Edit printer' : 'New printer'}
                  </h2>
                  <p className="text-xs font-mono text-gray-500 mt-1">{selected.group_id}</p>
                </div>
                <button type="button" onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
                {selected?.mobile_device_id ? (
                  <p className="text-xs text-amber-900 bg-amber-50 border border-amber-100 rounded-md px-2 py-1.5">
                    <span className="font-medium">Device-linked printer</span>
                    <span className="block font-mono break-all mt-0.5">{selected.mobile_device_id}</span>
                    <span className="text-[10px] text-amber-800/90">
                      Same fields as independent printers; this row is used only for that device in payout-config.
                    </span>
                  </p>
                ) : (
                  <p className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-md px-2 py-1.5">
                    <span className="font-medium text-slate-800">Independent printer</span>
                    <span className="block text-[10px] text-slate-600 mt-0.5">
                      Tenant default when a device has no device-specific row. You can link this row to one device
                      below (it will move from “Independent” to “Linked to a device”).
                    </span>
                  </p>
                )}
                {selected?.id && !selected?.mobile_device_id ? (
                  <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/80 p-3 space-y-2">
                    <div className="text-xs font-medium text-slate-700">Link to a device</div>
                    <p className="text-[10px] text-slate-500">
                      Assign this printer row to one floor device. If that device already has a linked printer, remove
                      or change it first.
                    </p>
                    <div className="flex flex-wrap items-end gap-2">
                      <select
                        className="flex-1 min-w-[200px] rounded-md border border-gray-300 px-2 py-2 text-xs font-mono"
                        value={linkDeviceId}
                        onChange={(e) => setLinkDeviceId(e.target.value)}
                        disabled={linking}
                      >
                        <option value="">Select device…</option>
                        {tenantDevices.map((d) => (
                          <option key={d.device_id} value={d.device_id}>
                            {d.device_id}
                            {d.registration_username ? ` · ${d.registration_username}` : ''}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        disabled={linking || !linkDeviceId.trim()}
                        onClick={linkIndependentToDevice}
                        className="rounded-lg bg-slate-800 px-3 py-2 text-xs font-medium text-white hover:bg-slate-900 disabled:opacity-50"
                      >
                        {linking ? '…' : 'Link'}
                      </button>
                    </div>
                  </div>
                ) : null}
                {selected?.id && selected?.mobile_device_id ? (
                  <button
                    type="button"
                    disabled={linking}
                    onClick={unlinkPrinterFromDevice}
                    className="text-xs text-slate-600 hover:text-slate-900 underline disabled:opacity-50"
                  >
                    Make independent again (unlink from device)
                  </button>
                ) : null}
                <PrinterSettingsFields form={form} onFieldChange={patchField} variant="tenantPrinters" />

                <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                  {selected.id ? (
                    <button
                      type="button"
                      onClick={() => remove(selected)}
                      className="inline-flex items-center gap-2 text-sm text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="h-4 w-4" />
                      Delete
                    </button>
                  ) : (
                    <span />
                  )}
                  <button
                    type="button"
                    disabled={saving}
                    onClick={save}
                    className="rounded-lg bg-psg-teal px-5 py-2 text-sm font-medium text-white hover:bg-psg-teal/90 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
